import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Box,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ColorLens as ColorLensIcon,
  Gradient as GradientIcon,
  Image as ImageIcon,
  Pattern as PatternIcon,
} from '@mui/icons-material';
import { GapStylingComponentProps, defaultConfig } from './types';
import BasicTab from './BasicTab';
import BackgroundTab from './BackgroundTab';
import StyleTab from './StyleTab';
import AdvancedTab from './AdvancedTab';
import PreviewSection from './PreviewSection';
import ActionButtons from './ActionButtons';

/**
 * Main gap styling component for configuring gap appearance between raffle items
 * Provides a comprehensive UI for customizing gap styling with multiple tabs
 */
const GapStylingComponent: React.FC<GapStylingComponentProps> = ({
  config,
  onConfigChange,
  onSave,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const updateConfig = (updates: Partial<typeof config>) => {
    onConfigChange({ ...config, ...updates });
  };

  const updateNestedConfig = <T extends keyof typeof config>(
    key: T,
    updates: Partial<typeof config[T]>
  ) => {
    const currentValue = config[key];
    if (typeof currentValue === 'object' && currentValue !== null) {
      onConfigChange({
        ...config,
        [key]: { ...currentValue, ...updates },
      });
    }
  };

  const handleImageUpload = (imageData: string) => {
    updateConfig({ backgroundImage: imageData });
  };

  const handleSave = async () => {
    if (!onSave) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await onSave(config);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide success message after 3 seconds
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save configuration');
      setTimeout(() => setSaveError(null), 5000); // Hide error message after 5 seconds
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic', icon: <ColorLensIcon /> },
    { id: 'background', label: 'Background', icon: <GradientIcon /> },
    { id: 'style', label: 'Style', icon: <PatternIcon /> },
    { id: 'advanced', label: 'Advanced', icon: <ImageIcon /> },
  ];

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title="Gap Styling Customization"
        action={
          <Button onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
        }
      />

      <Collapse in={expanded}>
        <CardContent>
          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={tab.icon}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Tab Content */}
          {activeTab === 'basic' && (
            <BasicTab config={config} onConfigChange={updateConfig} />
          )}
          {activeTab === 'background' && (
            <BackgroundTab
              config={config}
              onConfigChange={updateConfig}
              onImageUpload={handleImageUpload}
            />
          )}
          {activeTab === 'style' && (
            <StyleTab
              config={config}
              onConfigChange={updateConfig}
              onNestedConfigChange={updateNestedConfig}
            />
          )}
          {activeTab === 'advanced' && (
            <AdvancedTab
              config={config}
              onNestedConfigChange={updateNestedConfig}
            />
          )}

          {/* Preview */}
          <PreviewSection config={config} />

          {/* Action Buttons */}
          <ActionButtons
            config={config}
            onConfigChange={onConfigChange}
            onSave={onSave ? handleSave : undefined}
            saving={saving}
            defaultConfig={defaultConfig}
          />

          {/* Success/Error Messages */}
          <Snackbar
            open={saveSuccess}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert severity="success" onClose={() => setSaveSuccess(false)}>
              Gap styling configuration saved successfully!
            </Alert>
          </Snackbar>

          <Snackbar
            open={!!saveError}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Snackbar>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export { defaultConfig };
export default GapStylingComponent;